import React, { useState } from 'react'
import { DatePicker, Table, Tabs } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import { Moment } from 'moment'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import { getMemberBalanceConsumeRankList, getMemberRechargeRankList, getRealtimeBalanceList } from '../api'
import SettingPage, { SettingPreview, SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'

type BalanceTab = 'realtime' | 'recharge' | 'consume'

const formatAmount = (value: any) => `${Number(value || 0).toFixed(2)} 元`
const getMemberName = (member: any) => member?.name || member?.user?.nickname || '--'

const BalanceOperations: React.FC = () => {
    const [tab, setTab] = useState<BalanceTab>('realtime')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [date, setDate] = useState<string[]>([])
    const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>(null)
    const req = useRequest(
        async () => {
            const params = {
                page,
                limit: pageSize,
                from_date: date[0],
                to_date: date[1],
            }
            if (tab === 'realtime') {
                return getRealtimeBalanceList({ page, limit: pageSize, desc: true })
            }
            if (tab === 'recharge') return getMemberRechargeRankList(params)

            return getMemberBalanceConsumeRankList(params)
        },
        {
            refreshDeps: [tab, page, pageSize, date],
            onError: e => $msg('error', e.message || '获取余额业务数据失败'),
        },
    )

    const realtimeColumns: ColumnsType<any> = [
        {
            title: '排名',
            width: 80,
            render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
        },
        { title: '会员', render: (_value, record) => getMemberName(record) },
        { title: '当前余额', dataIndex: 'rt_balance', width: 150, render: formatAmount },
    ]
    const rankColumns: ColumnsType<any> = [
        {
            title: '排名',
            width: 80,
            render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
        },
        { title: '会员', dataIndex: 'member', render: getMemberName },
        { title: '手机号', dataIndex: ['member', 'phone'], width: 150, render: value => value || '--' },
        {
            title: tab === 'recharge' ? '累计充值' : '余额消费',
            dataIndex: tab === 'recharge' ? 'recharge_amount' : 'consume_amount',
            width: 150,
            render: formatAmount,
        },
        {
            title: tab === 'recharge' ? '充值次数' : '消费次数',
            dataIndex: tab === 'recharge' ? 'recharge_time' : 'consume_time',
            width: 130,
            render: value => value || 0,
        },
    ]
    const previewItems =
        tab === 'realtime'
            ? [
                  { label: '会员累计余额', value: formatAmount(req.data?.balance_sum) },
                  { label: '会员人均余额', value: formatAmount(req.data?.balance_avg) },
              ]
            : tab === 'recharge'
            ? [
                  { label: '累计充值', value: formatAmount(req.data?.recharge_amount_sum) },
                  { label: '充值会员', value: `${req.data?.recharge_member_count || 0} 人` },
                  { label: '充值次数', value: `${req.data?.recharge_time_count || 0} 次` },
                  { label: '人均充值', value: formatAmount(req.data?.recharge_amount_avg) },
              ]
            : [
                  { label: '累计消费', value: formatAmount(req.data?.consume_amount_sum) },
                  { label: '消费会员', value: `${req.data?.consume_member_count || 0} 人` },
                  { label: '消费次数', value: `${req.data?.consume_time_count || 0} 次` },
                  { label: '人均消费', value: formatAmount(req.data?.consume_amount_avg) },
              ]

    return (
        <SettingPage
            title="余额业务"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                <>
                    {tab !== 'realtime' ? (
                        <DatePicker.RangePicker
                            value={dateValue}
                            allowClear
                            suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                            onChange={(value, dateString) => {
                                setPage(1)
                                setDateValue(value)
                                setDate(dateString[0] ? dateString : [])
                            }}
                        />
                    ) : null}
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                </>
            }
        >
            <Tabs
                activeKey={tab}
                onChange={value => {
                    setTab(value as BalanceTab)
                    setPage(1)
                }}
            >
                <Tabs.TabPane tab="当前余额榜" key="realtime" />
                <Tabs.TabPane tab="累计充值榜" key="recharge" />
                <Tabs.TabPane tab="余额消费榜" key="consume" />
            </Tabs>
            <SettingPreview items={previewItems} />
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => record.id || record.member?.id || index}
                    loading={req.loading}
                    columns={tab === 'realtime' ? realtimeColumns : rankColumns}
                    dataSource={tab === 'realtime' ? req.data?.member_list || [] : req.data?.member_data_list || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default BalanceOperations
